import { Utils } from '@Util/utils'
import { MultiplierAnimation, MultiplierAnimationResult } from '../../multiplier-animation'
import { tweenPromise } from '@Animation/utils'
import {
  MI_MULTIPLIER_WHEEL_ATLAS_FRAMES,
  MI_MULTIPLIER_WHEEL_TEXTURE_KEYS,
} from './asset-preloader'
import {
  MI_MULTIPLIER_SHARED_ATLAS_FRAMES,
  MI_MULTIPLIER_SHARED_TEXTURE_KEYS,
} from '../shared/constants'

const WHEEL_OUTCOMES = [
  [Utils.degreesToRadians(18), Utils.degreesToRadians(198)],
  [Utils.degreesToRadians(90), Utils.degreesToRadians(270)],
  [Utils.degreesToRadians(162), Utils.degreesToRadians(342)],
  [Utils.degreesToRadians(54), Utils.degreesToRadians(234)],
  [Utils.degreesToRadians(126), Utils.degreesToRadians(306)],
]

export class MichiganWheelMultiplierAnimation extends MultiplierAnimation {
  container: Phaser.GameObjects.Container
  resultContainer: Phaser.GameObjects.Container
  background: Phaser.GameObjects.Image
  wheel: Phaser.GameObjects.Image
  blankWheel: Phaser.GameObjects.Image
  wheelBorder: Phaser.GameObjects.Image
  pointer: Phaser.GameObjects.Image
  wheelLights: Phaser.GameObjects.Image

  spotlightLeft: Phaser.GameObjects.Image
  spotlightRight: Phaser.GameObjects.Image

  targetOffset: number = 0
  lightAnimationLoopIndex: number = 0

  constructor(scene: Phaser.Scene, x: number, y: number, config: MultiplierAnimationResult) {
    super(scene, x, y, config)

    this.container = this.scene.add.container(0, 0)
    this.resultContainer = this.scene.add.container(0, 0)

    this.background = this.scene.add.image(
      0,
      0,
      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.BACKGROUND,
    )
    this.background.setOrigin(0.5, 0.5)

    const podium = this.scene.add.image(
      0,
      180,
      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.PODIUM,
    )

    const base = this.scene.add.image(
      0,
      108,
      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.WHEEL_BASE,
    )

    this.blankWheel = this.scene.add.image(
      0,
      -50,
      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.WHEEL_BLANK,
    )
    this.blankWheel.setOrigin(0.5, 0.5)

    this.wheel = this.scene.add.image(
      0,
      -50,
      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.WHEEL_STATIC,
    )
    this.wheel.setOrigin(0.5, 0.5)

    this.wheelBorder = this.scene.add.image(
      0,
      -50,
      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.WHEEL_OUTER_RING,
    )
    this.wheelBorder.setOrigin(0.5, 0.5)

    this.pointer = this.scene.add.image(
      0,
      -250,
      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.POINTER,
    )
    this.pointer.setOrigin(0.5, 0.0)

    this.wheelLights = this.scene.add.image(
      0,
      -50,
      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      `${MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.FLASH_PREFIX}1.png`,
    )
    this.wheelLights.setVisible(false)

    this.spotlightLeft = this.scene.add.image(
      -250,
      -250,
      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.SPOTLIGHT,
    )
    this.spotlightLeft.setAlpha(0.6)
    this.spotlightLeft.setRotation(-Math.PI / 8)
    this.spotlightLeft.setOrigin(0.5, 0)

    this.spotlightRight = this.scene.add.image(
      250,
      -250,
      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.SPOTLIGHT,
    )
    this.spotlightRight.setAlpha(0.6)
    this.spotlightRight.setRotation(Math.PI / 8)
    this.spotlightRight.setOrigin(0.5, 0)

    this.add([this.background, this.container])
    this.container.add([
      this.spotlightLeft,
      this.spotlightRight,
      podium,
      base,
      this.blankWheel,
      this.wheel,
      this.wheelBorder,
      this.wheelLights,
      this.pointer,
      this.resultContainer,
    ])
  }

  async play() {
    this.animateSpotlights()

    await this.showKickerLogo()
    await this.spin()
    await Utils.sleep(500)
    await this.displayResult()

    await Utils.sleep(1000)

    this.scene.tweens.killAll()

    this.destroy()
    return
  }

  async showKickerLogo() {
    const { width, height } = this.scene.sys.game.canvas

    const overlay = this.scene.add.graphics()
    overlay.fillStyle(0x000000, 0.5)
    overlay.fillRect(-width / 2, -height / 2, width, height)

    const heres = this.scene.add.image(
      -500,
      -100,
      MI_MULTIPLIER_SHARED_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_SHARED_ATLAS_FRAMES.HERES,
    )
    heres.setOrigin(0.5, 0.5)
    const the = this.scene.add.image(
      500,
      -25,
      MI_MULTIPLIER_SHARED_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_SHARED_ATLAS_FRAMES.THE,
    )
    the.setOrigin(0.5, 0.5)

    const kicker = this.scene.add.image(
      -250,
      -300,
      MI_MULTIPLIER_SHARED_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_SHARED_ATLAS_FRAMES.KICKER,
    )
    kicker.setOrigin(0.5, 0.5)
    kicker.alpha = 0

    const introDuration = 300

    const introHeres = tweenPromise(this.scene, {
      targets: heres,
      x: 10,
      duration: introDuration,
    })
    const introThe = tweenPromise(this.scene, {
      targets: the,
      x: -10,
      duration: introDuration,
    })
    const introKicker = tweenPromise(this.scene, {
      targets: kicker,
      x: 0,
      y: 50,
      alpha: 1,
      duration: introDuration,
    })

    this.add([overlay, kicker, heres, the])

    await Promise.all([introHeres(), introThe(), introKicker()])

    const driftDuration = 1100
    const driftHeres = tweenPromise(this.scene, {
      targets: heres,
      x: -25,
      duration: driftDuration,
    })

    const driftThe = tweenPromise(this.scene, {
      targets: the,
      x: 25,
      duration: driftDuration,
    })

    await Promise.all([driftHeres(), driftThe()])

    const outroDuration = 300

    const outroHeres = tweenPromise(this.scene, {
      targets: heres,
      x: 500,
      duration: outroDuration,
    })
    const outroThe = tweenPromise(this.scene, {
      targets: the,
      x: -500,
      duration: outroDuration,
    })
    const outroKicker = tweenPromise(this.scene, {
      targets: kicker,
      scale: 0.4,
      y: 200,
      alpha: 0,
      duration: outroDuration,
    })

    await Promise.all([outroHeres(), outroThe(), outroKicker()])

    heres.destroy()
    the.destroy()
    kicker.destroy()
    overlay.destroy()
  }

  animateSpotlights() {
    tweenPromise(this.scene, {
      targets: this.spotlightLeft,
      rotation: (-3 * Math.PI) / 8,
      duration: 1000,
      yoyo: true,
      repeat: -1,
    })()

    tweenPromise(this.scene, {
      targets: this.spotlightRight,
      rotation: (3 * Math.PI) / 8,
      duration: 1000,
      yoyo: true,
      repeat: -1,
    })()
  }

  async spin() {
    this.targetOffset = Utils.getRandomElementFromArray([
      Utils.degreesToRadians(13),
      Utils.degreesToRadians(-13),
    ])
    const target =
      Utils.getRandomElementFromArray(WHEEL_OUTCOMES[this.multiplierValue - 1]) + this.targetOffset

    const standardLoopTime = 600

    const startSpin = tweenPromise(this.scene, {
      targets: this.wheel,
      rotation: 4 * Math.PI,
      duration: 2.6 * standardLoopTime,
      ease: Phaser.Math.Easing.Back.In,
    })

    const endSpin = tweenPromise(this.scene, {
      targets: this.wheel,
      rotation: 4 * Math.PI + target,
      duration: 4 * standardLoopTime + target / (2 * Math.PI),
      ease: Phaser.Math.Easing.Quartic.Out,
    })

    tweenPromise(this.scene, {
      targets: this.container,
      scale: 2,
      y: 200,
      duration: 4000,
    })()

    tweenPromise(this.scene, {
      targets: this.background,
      scale: 0.67,
      duration: 4000,
    })()

    this.flickerPointer()

    await startSpin()
    await endSpin()
  }

  async flickerPointer() {
    await tweenPromise(this.scene, {
      targets: this.pointer,
      rotation: Math.PI / 6,
      duration: 500,
      yoyo: true,
    })()

    await tweenPromise(this.scene, {
      targets: this.pointer,
      rotation: -Math.PI / 7,
      duration: 100,
      yoyo: true,
      repeat: 6,
    })()

    await tweenPromise(this.scene, {
      targets: this.pointer,
      rotation: -Math.PI / 7,
      duration: 250,
      yoyo: true,
      repeat: 1,
    })()
  }

  async cycleLights() {
    this.wheelLights.setVisible(true)

    tweenPromise(this.scene, {
      targets: this.wheelLights,
      onLoop: () => {
        this.lightAnimationLoopIndex = (this.lightAnimationLoopIndex + 1) % 3
        this.wheelLights.setTexture(
          MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
          `${MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.FLASH_PREFIX}${this.lightAnimationLoopIndex + 1}.png`,
        )
      },
      loop: -1,
      loopDelay: 200,
    })()
  }

  async displayResult() {
    this.cycleLights()
    const number = this.scene.add.image(
      this.targetOffset > 0 ? -8 : 8,
      -157,

      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      `${this.multiplierValue}${MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.NUMBER_GLOW_SUFFIX}`,
    )
    number.setOrigin(0.5, 0.5)

    const x = this.scene.add.image(
      this.targetOffset > 0 ? -8 : 8,
      -116,

      MI_MULTIPLIER_WHEEL_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_WHEEL_ATLAS_FRAMES.X_GLOW,
    )
    x.setOrigin(0.5, 0.5)

    const { width, height } = this.scene.sys.game.canvas

    const overlay = this.scene.add.graphics()
    overlay.fillStyle(0x000000, 0.15)
    overlay.fillRect(-width / 2, -height / 2, width, height)
    overlay.setAlpha(0)

    number.setScale(0.33)
    x.setScale(0.33)

    this.resultContainer.setRotation(this.targetOffset)
    this.resultContainer.add([overlay, number, x])
    this.blankWheel.setRotation(this.wheel.rotation)

    const hidePrizes = tweenPromise(this.scene, {
      targets: this.wheel,
      alpha: 0,
      duration: 250,
    })

    const showWinningWedge = tweenPromise(this.scene, {
      targets: [overlay],
      alpha: 1,
      duration: 250,
    })

    await Promise.all([hidePrizes(), showWinningWedge()])

    //Bring To Center
    const centerNumber = tweenPromise(this.scene, {
      targets: number,
      x: -35,
      y: -110,
      scale: 1.0,
      duration: 1000,
    })

    const centerX = tweenPromise(this.scene, {
      targets: x,
      x: 40,
      y: -85,
      scale: 1.0,
      duration: 1000,
    })

    const rotateAlign = tweenPromise(this.scene, {
      targets: this.resultContainer,
      rotation: 0,
      duration: 500,
    })

    await Promise.all([centerNumber(), centerX(), rotateAlign()])
  }

  destroy(fromScene?: boolean): void {
    this.container.removeAll(true)
    this.resultContainer.removeAll(true)
    super.destroy(fromScene)
  }
}
